import React from 'react';
import SEO from '../components/SEO';

const airlinesJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'US Airlines on Flyairlinebooking',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'American Airlines', url: 'https://flyairlinebooking.com/flights?origin=DFW' },
    { '@type': 'ListItem', position: 2, name: 'Delta Air Lines',   url: 'https://flyairlinebooking.com/flights?origin=ATL' },
    { '@type': 'ListItem', position: 3, name: 'United Airlines',   url: 'https://flyairlinebooking.com/flights?origin=ORD' },
    { '@type': 'ListItem', position: 4, name: 'JetBlue Airways',   url: 'https://flyairlinebooking.com/flights?origin=JFK' },
    { '@type': 'ListItem', position: 5, name: 'Southwest Airlines',url: 'https://flyairlinebooking.com/flights?origin=DAL' },
  ],
};

const AIRLINES = [
  {
    code: 'AA', name: 'American Airlines', hub: 'Dallas/Fort Worth (DFW)', founded: 1926,
    fleet: '900+', routes: '350+', alliance: 'Oneworld', color: 'bg-red-600', href: '/aa/airlines',
    desc: "The world's largest airline by fleet size. Extensive domestic and international network.",
    strengths: ['Largest US domestic network', 'Oneworld alliance benefits', 'AAdvantage rewards program'],
  },
  {
    code: 'DL', name: 'Delta Air Lines', hub: 'Atlanta (ATL)', founded: 1924,
    fleet: '850+', routes: '300+', alliance: 'SkyTeam', color: 'bg-blue-700', href: '/dl/airlines',
    desc: 'Consistently ranked among the most reliable US airlines with a premium experience.',
    strengths: ['Industry-leading on-time performance', 'SkyMiles loyalty program', 'Delta One business class'],
  },
  {
    code: 'UA', name: 'United Airlines', hub: 'Chicago O\'Hare (ORD)', founded: 1926,
    fleet: '800+', routes: '340+', alliance: 'Star Alliance', color: 'bg-blue-900', href: '/ua/airlines',
    desc: "One of the world's largest airlines with the most comprehensive transatlantic network.",
    strengths: ['Largest Star Alliance member', 'MileagePlus program', 'Polaris business class'],
  },
  {
    code: 'SW', name: 'Southwest Airlines', hub: 'Dallas Love Field (DAL)', founded: 1967,
    fleet: '700+', routes: '120+', alliance: 'None', color: 'bg-emerald-500',
    desc: 'The king of low-cost domestic travel. No change fees, 2 free checked bags.',
    strengths: ['No change or cancellation fees', '2 free checked bags', 'Rapid Rewards program'],
  },
  {
    code: 'B6', name: 'JetBlue Airways', hub: 'New York JFK (JFK)', founded: 1998,
    fleet: '280+', routes: '100+', alliance: 'None', color: 'bg-cyan-600', href: '/b6/airlines',
    desc: 'Premium low-cost carrier known for free Wi-Fi, extra legroom and live TV.',
    strengths: ['Free high-speed Wi-Fi on all flights', 'More legroom than competitors', 'TrueBlue rewards'],
  },
  {
    code: 'AS', name: 'Alaska Airlines', hub: 'Seattle-Tacoma (SEA)', founded: 1932,
    fleet: '330+', routes: '115+', alliance: 'Oneworld', color: 'bg-teal-600',
    desc: 'Award-winning West Coast carrier, top-ranked for customer satisfaction.',
    strengths: ['#1 in customer satisfaction', 'Mileage Plan program', 'West Coast coverage'],
  },
  {
    code: 'F9', name: 'Frontier Airlines', hub: 'Denver (DEN)', founded: 1994,
    fleet: '120+', routes: '100+', alliance: 'None', color: 'bg-green-600',
    desc: 'Ultra-low-cost carrier offering some of the cheapest base fares in the US.',
    strengths: ['Lowest base fares', 'FRONTIER Miles program', 'Growing route network'],
  },
  {
    code: 'NK', name: 'Spirit Airlines', hub: 'Fort Lauderdale (FLL)', founded: 1983,
    fleet: '185+', routes: '85+', alliance: 'None', color: 'bg-yellow-500',
    desc: 'Ultra-low-cost with bare-bones fares — perfect for budget-first travelers.',
    strengths: ['Lowest headline fares', 'Free Spirit loyalty program', 'Flexible add-ons'],
  },
];

export default function AirlinesPage() {
  const today = new Date();
  today.setDate(today.getDate() + 14);
  const date = today.toISOString().split('T')[0];

  return (
    <div>
      {/* Trademark Disclaimer */}
      <div className="bg-amber-50 border-b border-amber-100 py-3 px-4 text-center text-xs text-amber-800">
        <strong>Airline Trademark Disclaimer:</strong> Airline names, logos, and trademarks are the property of their respective owners and are used for identification purposes only. Flyairlinebooking is not affiliated with, endorsed by, or associated with any airline.
      </div>
      <SEO
        title="Airlines"
        canonical="/airlines"
        description="Compare all major US airlines on Flyairlinebooking — American, Delta, United, JetBlue, Southwest, Alaska, Spirit and Frontier. Find the best fares and book instantly."
        keywords="US airlines, compare airlines, American Airlines flights, Delta flights, United Airlines, JetBlue, Southwest Airlines, cheap airline tickets"
        jsonLd={airlinesJsonLd}
      />
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-900 to-green-900 text-white py-20 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">Our Airline Partners</h1>
        <p className="text-blue-100 text-lg max-w-xl mx-auto">
          We partner with all major US carriers so you always get the best selection and price.
        </p>
      </div>

      {/* Summary strip */}
      <div className="bg-white border-b border-gray-100 py-6 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4 sm:gap-6 text-center">
          <div><p className="text-2xl font-extrabold text-blue-600">8</p><p className="text-gray-500 text-sm">Airline Partners</p></div>
          <div><p className="text-2xl font-extrabold text-blue-600">500+</p><p className="text-gray-500 text-sm">US Routes</p></div>
          <div><p className="text-2xl font-extrabold text-blue-600">4,000+</p><p className="text-gray-500 text-sm">Daily Flights</p></div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10 sm:py-12 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {AIRLINES.map(airline => (
          <div key={airline.code} className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className={`${airline.color} px-6 py-4 flex items-center justify-between`}>
              <div>
                <p className="text-white font-extrabold text-xl">{airline.name}</p>
                <p className="text-white/70 text-sm">Hub: {airline.hub}</p>
              </div>
              <span className="bg-white/20 text-white text-lg font-bold px-3 py-1 rounded-lg">{airline.code}</span>
            </div>
            <div className="p-5">
              <p className="text-gray-600 text-sm mb-4 leading-relaxed">{airline.desc}</p>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4 text-center">
                {[
                  { label: 'Founded', value: airline.founded },
                  { label: 'Fleet', value: airline.fleet },
                  { label: 'Routes', value: airline.routes },
                ].map(stat => (
                  <div key={stat.label} className="bg-gray-50 rounded-xl py-2 px-1">
                    <p className="font-bold text-gray-800 text-sm">{stat.value}</p>
                    <p className="text-gray-400 text-xs">{stat.label}</p>
                  </div>
                ))}
              </div>
              <ul className="space-y-1 mb-5">
                {airline.strengths.map(s => (
                  <li key={s} className="text-xs text-gray-600 flex items-center gap-2">
                    <span className="text-green-500 font-bold">✓</span>{s}
                  </li>
                ))}
              </ul>
              <a
                href={airline.href || `/flights?origin=JFK&destination=LAX&date=${date}&passengers=1&class=economy`}
                className="block text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
              >
                {airline.href ? `View ${airline.name} Flights →` : `Search ${airline.name} Flights →`}
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
